import React, { useMemo, useState } from "react";
import { Button, DialogActions, DialogTitle } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Day, TimeRange } from "./types";
import {
  END_HOUR_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
} from "./constants";
import { slotIndexToTime, timeToSlotIndex } from "./utils";
import TimeWheelPicker from "./TimeWheelPicker";
import {
  ModalContent,
  ModalDayLabel,
  ModalErrorWrapper,
  ModalRow,
  StyledDialog,
} from "./WeeklyCalendarEventModal.styles";

interface WeeklyCalendarEventModalProps {
  day: Day;
  // Evento de partida: el que se edita o el propuesto al crear.
  initialEvent: TimeRange;
  // Índice del evento que se edita; -1 al crear uno nuevo.
  eventIndex: number;
  // Eventos existentes del día, para detectar colisiones.
  dayEvents: TimeRange[];
  range: { minSlot: number; maxSlot: number } | null;
  onSave: (event: TimeRange) => void;
  onClose: () => void;
}

const WeeklyCalendarEventModal: React.FC<WeeklyCalendarEventModalProps> = ({
  day,
  initialEvent,
  eventIndex,
  dayEvents,
  range,
  onSave,
  onClose,
}) => {
  const [sH, setSH] = useState(initialEvent[0].split(":")[0]);
  const [sM, setSM] = useState(initialEvent[0].split(":")[1]);
  const [eH, setEH] = useState(initialEvent[1].split(":")[0]);
  const [eM, setEM] = useState(initialEvent[1].split(":")[1]);

  const isNew = eventIndex === -1;

  const startHours = useMemo(
    () =>
      range
        ? HOUR_OPTIONS.filter((h) => {
            const n = parseInt(h);
            return (
              n >= Math.floor(range.minSlot / 2) &&
              n <= Math.floor((range.maxSlot - 1) / 2)
            );
          })
        : HOUR_OPTIONS,
    [range]
  );
  const endHours = useMemo(
    () =>
      range
        ? END_HOUR_OPTIONS.filter((h) => {
            const n = parseInt(h);
            return (
              n >= Math.floor(range.minSlot / 2) &&
              n <= Math.floor(range.maxSlot / 2)
            );
          })
        : END_HOUR_OPTIONS,
    [range]
  );
  // A las 24:00 el único minuto válido es 00.
  const endMinutes = eH === "24" ? ["00"] : MINUTE_OPTIONS;

  const startIdx = timeToSlotIndex(`${sH}:${sM}`);
  const endIdx = timeToSlotIndex(`${eH}:${eM}`);

  let error: string | null = null;
  if (startIdx >= endIdx) {
    error = "La hora de fin debe ser posterior a la de inicio.";
  } else if (range && (startIdx < range.minSlot || endIdx > range.maxSlot)) {
    error = "La franja está fuera del horario disponible.";
  } else if (
    dayEvents.some((r, idx) => {
      if (idx === eventIndex) return false;
      const s = timeToSlotIndex(r[0]);
      const e = timeToSlotIndex(r[1]);
      return startIdx < e && s < endIdx;
    })
  ) {
    error = "Esta franja se solapa con otra del mismo día.";
  }

  const handleStartChange = (hour: string, minute: string) => {
    setSH(hour);
    setSM(minute);
  };

  const handleEndChange = (hour: string, minute: string) => {
    setEH(hour);
    setEM(hour === "24" ? "00" : minute);
  };

  const handleSave = () => {
    if (error) return;
    onSave([slotIndexToTime(startIdx), slotIndexToTime(endIdx)]);
  };

  return (
    <StyledDialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isNew ? "Añadir franja horaria" : "Editar franja horaria"}
      </DialogTitle>
      <ModalContent>
        <ModalDayLabel>{day.fullLabel}</ModalDayLabel>
        <ModalRow>
          <TimeWheelPicker
            label="Desde"
            hour={sH}
            minute={sM}
            hourOptions={startHours}
            minuteOptions={MINUTE_OPTIONS}
            onChange={handleStartChange}
          />
          <TimeWheelPicker
            label="Hasta"
            hour={eH}
            minute={eM}
            hourOptions={endHours}
            minuteOptions={endMinutes}
            onChange={handleEndChange}
          />
        </ModalRow>
        {error && (
          <ModalErrorWrapper>
            <Alert severity="error">{error}</Alert>
          </ModalErrorWrapper>
        )}
      </ModalContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!!error}
          onClick={handleSave}
        >
          Guardar
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default WeeklyCalendarEventModal;
